import { useTranslations } from "next-intl";
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@repo/ui/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@repo/ui/lib/utils";
import { useAutoSubmit } from "@/hooks/useAutoSubmit";
import { useSizes } from "@/data/sizes";
import { Size } from "@/types/size";
import { usePathname } from "next/navigation";
import { ScrollArea } from "@repo/ui/components/ui/scroll-area";

const FormSchema = z.object({
  size: z.array(z.string()).refine((value) => value.some((item) => item), {}),
});

export const FiltersMenuItemSizes = ({
  applyFilter,
  initialValue,
}: {
  applyFilter: (data: { size: string[] } | null) => void;
  initialValue: string[];
}) => {
  const path = usePathname();
  const categoryId = path.split("/").at(-2);
  const t = useTranslations("Products-list.filters");
  const { data: sizes } = useSizes(categoryId!);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      size: [],
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    applyFilter(data);
  }

  useEffect(() => {
    form.setValue("size", initialValue);
  }, [initialValue]);

  useAutoSubmit({
    trigger: form.trigger,
    watch: form.watch,
    onSubmit: form.handleSubmit(onSubmit),
  });

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1">
        <div className="py-[16px]">
          <AccordionTrigger className="max-md:text-base font-medium py-0 data-[state=open]:text-primary-magenta">
            {t("size")}
          </AccordionTrigger>
          <AccordionContent className="mt-[20px] p-0">
            <ScrollArea className="h-[400px]">
              <div className="pb-6 text-left w-full">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-8"
                  >
                    <FormField
                      control={form.control}
                      name="size"
                      render={() => (
                        <FormItem>
                          <div className="grid grid-cols-3 gap-4 items-stretch pr-4">
                            {sizes &&
                              sizes.map((item: Size) => (
                                <FormField
                                  key={item.size}
                                  control={form.control}
                                  name="size"
                                  render={({ field }) => {
                                    return (
                                      <FormItem key={item.size}>
                                        <FormControl>
                                          <FilterMenuItemSizesCheckbox
                                            size={item.size}
                                            checked={field.value?.includes(
                                              item.size,
                                            )}
                                            onCheckedChange={(checked) => {
                                              return checked
                                                ? field.onChange([
                                                    ...field.value,
                                                    item.size,
                                                  ])
                                                : field.onChange(
                                                    field.value?.filter(
                                                      (value) =>
                                                        value !== item.size,
                                                    ),
                                                  );
                                            }}
                                          />
                                        </FormControl>
                                      </FormItem>
                                    );
                                  }}
                                />
                              ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </div>
            </ScrollArea>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
};

type FilterMenuItemSizesCheckboxProps = {
  size: string;
} & Omit<ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "size">;

const FilterMenuItemSizesCheckbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  FilterMenuItemSizesCheckboxProps
>(({ className, size, ...props }, ref) => {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "w-full border border-secondary-gray1 p-2 cursor-pointer h-full",
        { "font-bold border-2 border-primary-magenta": props.checked },
        className,
      )}
      {...props}
    >
      {size}
    </CheckboxPrimitive.Root>
  );
});
